import { DotLottie } from 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web/+esm';

class AboutStory extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this._initLottie();

    const setup = () => requestAnimationFrame(() => this._setupScroll());
    if (document.readyState === 'complete') setup();
    else window.addEventListener('load', setup, { once: true });
  }

  _initLottie() {
    const stack = this.querySelector('[data-lottie-stack]');
    if (!stack) return;

    this._lottieInstances = [];
    this._lottieReady = false;
    const canvases = stack.querySelectorAll('canvas[data-lottie-src]');
    const noiseOverlay = stack.querySelector('[data-noise-overlay]');
    const maxDpr = Math.min(window.devicePixelRatio || 1, 1.5);

    const sizeCanvases = () => {
      const w = stack.clientWidth;
      const px = Math.round(w * maxDpr);
      canvases.forEach(c => { c.width = px; c.height = px; });
      if (noiseOverlay) { noiseOverlay.width = px; noiseOverlay.height = px; }
      this._generateNoise(px, px);
    };

    sizeCanvases();

    let loadCount = 0;

    canvases.forEach(canvas => {
      const src = canvas.dataset.lottieSrc;
      if (!src) return;
      const instance = new DotLottie({ canvas, src, autoplay: false, loop: false });
      this._lottieInstances.push(instance);

      instance.addEventListener('load', () => {
        loadCount++;
        if (loadCount >= this._lottieInstances.length) {
          this._lottieReady = true;
          this._setLottieProgress(0);
        }
      });
    });

    if (noiseOverlay) this._startNoiseLoop(stack, noiseOverlay);

    const observer = new IntersectionObserver((entries) => {
      this._visible = entries[0].isIntersecting;
      if (this._visible) {
        this._noiseActive = true;
        if (this._noiseDraw && !this._noiseRaf) {
          this._noiseRaf = requestAnimationFrame(this._noiseDraw);
        }
      } else {
        this._noiseActive = false;
        if (this._noiseRaf) {
          cancelAnimationFrame(this._noiseRaf);
          this._noiseRaf = null;
        }
      }
    }, { threshold: 0.05 });
    observer.observe(stack);

    let resizeId;
    window.addEventListener('resize', () => {
      clearTimeout(resizeId);
      resizeId = setTimeout(() => {
        sizeCanvases();
        this._lottieInstances.forEach(inst => inst.resize());
      }, 200);
    });
  }

  _setLottieProgress(progress) {
    if (!this._lottieReady) return;
    const p = Math.max(0, Math.min(1, progress));
    this._lottieInstances.forEach(inst => {
      const total = inst.totalFrames;
      if (!total) return;
      inst.setFrame(Math.round(p * (total - 1)));
    });
  }

  _generateNoise(w, h) {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(w, h);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    this._noiseSource = canvas;
  }

  _startNoiseLoop(stack, overlay) {
    const pillsCanvas = stack.querySelector('[data-lottie-layer="pills"]');
    if (!pillsCanvas) return;

    const ctx = overlay.getContext('2d');
    this._noiseActive = true;
    let frame = 0;

    const draw = () => {
      if (!this._noiseActive) { this._noiseRaf = null; return; }
      this._noiseRaf = requestAnimationFrame(draw);
      if (++frame % 4 !== 0) return;

      const w = overlay.width;
      const h = overlay.height;
      if (!w || !h) return;

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.drawImage(pillsCanvas, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      if (this._noiseSource) ctx.drawImage(this._noiseSource, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
    };

    this._noiseDraw = draw;
    this._noiseRaf = requestAnimationFrame(draw);
  }

  _setupScroll() {
    this._clip = this.querySelector('[data-text-clip]');
    this._scrollText = this.querySelector('[data-scroll-text]');
    this._frame = this.querySelector('[data-scroll-frame]');
    if (!this._clip || !this._scrollText) return;

    if (window.innerWidth >= 1024) {
      this._setupDesktopScroll();
    } else {
      this._setupMobileScroll();
    }
  }

  _setupDesktopScroll() {
    if (!this._frame) return;

    this._scrollText.style.willChange = 'transform';
    this._computeScrollBounds();
    this._cacheFramePosition();
    this._scrollText.style.transform = `translate3d(0,${this._startY}px,0)`;

    window.addEventListener('scroll', () => {
      if (this._frameRange <= 0) return;
      const progress = Math.max(0, Math.min(1, (window.scrollY - this._frameTop) / this._frameRange));
      const y = this._startY + (this._endY - this._startY) * progress;
      this._scrollText.style.transform = `translate3d(0,${y}px,0)`;
      this._setLottieProgress(progress);
    }, { passive: true });

    let resizeId;
    window.addEventListener('resize', () => {
      clearTimeout(resizeId);
      resizeId = setTimeout(() => {
        this._computeScrollBounds();
        this._cacheFramePosition();
      }, 200);
    });
  }

  _setupMobileScroll() {
    const ov = Math.max(0, this._scrollText.offsetHeight - this._clip.clientHeight);
    if (ov <= 0) return;

    this._scrollText.style.willChange = 'transform';
    this._scrollText.style.transition = 'transform 0.15s linear';

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const rect = this._clip.getBoundingClientRect();
        const vh = window.innerHeight;
        const startAt = vh * 0.65;
        const endAt = vh * 0.05;
        const progress = Math.max(0, Math.min(1, (startAt - rect.top) / (startAt - endAt)));
        this._scrollText.style.transform = `translate3d(0,${-(ov * progress)}px,0)`;
        this._setLottieProgress(progress);
      });
    }, { passive: true });
  }

  _computeScrollBounds() {
    const cH = this._clip.clientHeight;
    const ov = Math.max(0, this._scrollText.offsetHeight - cH);
    const pad = Math.round(cH * 0.35);
    const framePad = parseInt(getComputedStyle(this._frame).paddingTop) || 0;

    this._frame.style.height = (window.innerHeight + pad + ov + pad + framePad) + 'px';
    this._startY = pad;
    this._endY = -(ov + pad);
  }

  _cacheFramePosition() {
    const rect = this._frame.getBoundingClientRect();
    this._frameTop = rect.top + window.scrollY;
    this._frameRange = rect.height - window.innerHeight;
  }
}

customElements.define('about-story', AboutStory);
